<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'

import { useEditorStore } from '@/stores'
import {
  Button,
  ComboboxSelect,
  DialogClose,
  DialogFooter,
  DialogWrapper,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TagsAutocomplete,
} from '@/ui'

const TITLES = {
  variable: 'Insert Variable',
  if: 'Insert If Condition',
  for: 'Insert For Loop',
  tableLoop: 'Insert Table Loop',
}

const editorStore = useEditorStore()
const { dynamicDialogCallback } = storeToRefs(editorStore)
const {
  dynamicDialogType,
  dynamicDialogAttrs,
  dynamicPlaceholders,
  dynamicPlaceholdersFor,
  dynamicPlaceholdersForValues,
  dynamicPlaceholdersLoading,
} = storeToRefs(editorStore)

const variableName = ref('')
const variableFilter = ref('')
const variableFilterArg = ref('')

const ifCondition = ref('')

const forVarArray = ref('')
const forVarField = ref('')

const tableLoopArray = ref('')
const tableColumnTitles = ref('')
const tableColumnFields = ref<string[]>([])

const isDisabled = computed(() => {
  if (dynamicDialogType.value === 'variable') {
    return !variableName.value
  }
  if (dynamicDialogType.value === 'if') {
    return !ifCondition.value
  }
  if (dynamicDialogType.value === 'for') {
    return !forVarArray.value
  }
  if (dynamicDialogType.value === 'tableLoop') {
    return !tableLoopArray.value || !tableColumnTitles.value || tableColumnFields.value.length === 0
  }
  return true
})

watch(
  [dynamicDialogType, dynamicDialogAttrs],
  ([type, attrs]) => {
    if (!type) return
    if (type === 'variable') {
      variableName.value = attrs?.name ?? ''
      variableFilter.value = attrs?.filter ?? ''
      variableFilterArg.value = attrs?.filterArg ?? ''
    }
    if (type === 'if') {
      ifCondition.value = attrs?.condition ?? ''
    }
    if (type === 'for') {
      forVarArray.value = attrs?.arrayName ?? ''
      forVarField.value = attrs?.arrayField ?? ''
    }
    if (type === 'tableLoop') {
      tableLoopArray.value = attrs?.arrayName ?? ''
      tableColumnTitles.value = attrs?.columnTitles ? attrs.columnTitles.join(', ') : ''
      tableColumnFields.value = attrs?.columnFields ?? []
    }
  },
  { immediate: true }
)

function handleDialogConfirm(close: () => void) {
  if (!dynamicDialogCallback.value) return

  if (dynamicDialogType.value === 'variable') {
    dynamicDialogCallback.value({
      name: variableName.value,
      filter: variableFilter.value || undefined,
      filterArg: variableFilterArg.value || undefined,
    })
  }
  if (dynamicDialogType.value === 'if') {
    dynamicDialogCallback.value({ condition: ifCondition.value })
  }
  if (dynamicDialogType.value === 'for') {
    dynamicDialogCallback.value({
      arrayName: forVarArray.value,
      arrayField: forVarField.value,
    })
  }
  if (dynamicDialogType.value === 'tableLoop') {
    const columnTitles = tableColumnTitles.value
      .split(',')
      .map(t => t.trim())
      .filter(t => t.length > 0)
    dynamicDialogCallback.value({
      arrayName: tableLoopArray.value,
      columnTitles,
      columnFields: tableColumnFields.value,
    })
  }

  close()
}

function onClose() {
  dynamicDialogType.value = null
  dynamicDialogAttrs.value = undefined
  dynamicDialogCallback.value = null
}
</script>

<template>
  <DialogWrapper :default-open="!!dynamicDialogType" :callback="onClose" :title="TITLES[dynamicDialogType]">
    <template #content="{ close }">
      <div class="mb-4 grid gap-y-4">
        <template v-if="dynamicDialogType === 'variable'">
          <div class="grid gap-y-2">
            <Label for="variableName">Variable name</Label>
            <ComboboxSelect
              v-model="variableName"
              :select-options="{ options: dynamicPlaceholders }"
              :max-items="50"
              :is-loading="dynamicPlaceholdersLoading"
              placeholder="Find or select variable name"
              class-names="bg-white"
            />
          </div>
          <div>
            <Label for="variableFilter">Filter type</Label>
            <Select id="variableFilter" v-model="variableFilter">
              <SelectTrigger>
                <SelectValue placeholder="Select filter type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Default Text</SelectItem>
                <SelectItem value="date">Date</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label for="variableFilterArg">Filter Arg</Label>
            <Input
              id="variableFilterArg"
              v-model="variableFilterArg"
              :disabled="!variableFilter"
              placeholder='e.g. "Some default"'
            />
          </div>
        </template>
        <template v-else-if="dynamicDialogType === 'if'">
          <div class="grid gap-y-2">
            <Label for="ifCondition">If Condition</Label>
            <ComboboxSelect
              v-model="ifCondition"
              :select-options="{ options: dynamicPlaceholders }"
              :max-items="50"
              :is-loading="dynamicPlaceholdersLoading"
              placeholder="Find or select condition"
              class-names="bg-white"
            />
          </div>
        </template>
        <template v-else-if="dynamicDialogType === 'for'">
          <div class="grid gap-y-2">
            <Label for="forVarArray">Loop Array</Label>
            <ComboboxSelect
              v-model="forVarArray"
              :select-options="{ options: dynamicPlaceholdersFor }"
              :max-items="50"
              :is-loading="dynamicPlaceholdersLoading"
              placeholder="Select array"
              class-names="bg-white"
            />
          </div>
          <div class="grid gap-y-2">
            <Label for="forVarField">Loop Field</Label>
            <Select id="forVarField" v-model="forVarField">
              <SelectTrigger>
                <SelectValue placeholder="Select field" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="field in dynamicPlaceholdersForValues" :key="field" :value="field">
                  {{ field }}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </template>
        <template v-else-if="dynamicDialogType === 'tableLoop'">
          <div class="grid gap-y-2">
            <Label for="tableLoopArray">Loop Array</Label>
            <ComboboxSelect
              v-model="tableLoopArray"
              :select-options="{ options: dynamicPlaceholdersFor }"
              :max-items="50"
              :is-loading="dynamicPlaceholdersLoading"
              placeholder="Select array (e.g., invoice.items)"
              class-names="bg-white"
            />
          </div>
          <div class="grid gap-y-2">
            <Label for="tableColumnTitles">Column Titles (comma-separated)</Label>
            <Input
              id="tableColumnTitles"
              v-model="tableColumnTitles"
              placeholder="e.g., #, Name, Quantity, Price, Total"
            />
          </div>
          <div class="grid gap-y-2">
            <Label for="tableColumnFields">Column Fields</Label>
            <TagsAutocomplete
              id="tableColumnFields"
              v-model="tableColumnFields"
              :available-tags="dynamicPlaceholdersForValues"
              placeholder="Select fields for each column..."
              empty-message="No fields available"
              class-names="bg-white"
            />
          </div>
        </template>
      </div>
      <DialogFooter class="grid w-full gap-2 sm:grid-cols-2">
        <DialogClose as-child>
          <Button variant="outline" size="sm">Cancel</Button>
        </DialogClose>
        <Button size="sm" :disabled="isDisabled" @click="handleDialogConfirm(close)">Insert</Button>
      </DialogFooter>
    </template>
  </DialogWrapper>
</template>
